/* ==========================================================================
   Cross-sell popup

   Shows a merchant-configured offer popup when a product is added to the cart.
   Matching is done server side by sections/cross-sell-popup.liquid; this file
   only decides when to ask, holds the cart drawer back while the popup is on
   screen, and releases it afterwards.

   Notes on timing
   ---------------
   The theme dispatches its local 'add-to-cart' event immediately before the
   document level 'krown:cart:add' event, and the cart drawer opens on the
   former. That means a listener on 'krown:cart:add' is already too late to
   stop the drawer. So the decision to intercept is made on the form's submit
   event, in the capture phase, which runs well before either.
   ========================================================================== */

(() => {
  if (window.KTCrossSell) return;

  const SELECTORS = {
    config: '[data-js-cross-sell-config]',
    modal: '[data-js-cross-sell-modal]',
    content: '[data-js-cross-sell-content]',
    drawer: '#site-cart-sidebar',
    cartCount: '#AjaxCartForm [data-cart-count]',
    headerCount: '[data-header-cart-count]',
    card: '[data-js-upsell-card]',
    continueButton: '[data-js-cross-sell-continue]',
    dismissButton: '[data-js-cross-sell-dismiss]',
  };

  const STORAGE_KEY = 'kt-cross-sell-last-shown';
  const COUNT_KEY = 'kt-cross-sell-add-count';

  const state = {
    config: null,
    // The form we intend to act on. Consumed once, on the first cart:add that
    // follows — so an add made from inside the popup cannot re-trigger it.
    armedForm: null,
    // Separate from armedForm because the drawer stays held for as long as the
    // popup is on screen, which is long after armedForm has been consumed.
    holdDrawer: false,
    countBefore: 0,
    drawerWasRequested: false,
    forceDrawerOnClose: false,
    failsafeTimer: null,
    cache: new Map(),
    // Whether a product has a live offer at all, keyed by product URL.
    // Populated by prefetching on intent, which is what lets the decision to
    // hold the cart drawer be made synchronously at submit time.
    matchCache: new Map(),
    busy: false,
    popupOpen: false,
    // Bumped whenever the situation changes underneath an in-flight offer
    // request, so a late response can be discarded instead of showing an
    // offer that no longer reflects the cart.
    generation: 0,
  };

  /* ----------------------------------------------------------------------
     Config
     ---------------------------------------------------------------------- */

  const readConfig = () => {
    const node = document.querySelector(SELECTORS.config);
    if (!node) {
      state.config = null;
      return null;
    }
    try {
      state.config = JSON.parse(node.textContent);
    } catch (e) {
      state.config = null;
    }
    return state.config;
  };

  /* ----------------------------------------------------------------------
     Frequency capping
     ---------------------------------------------------------------------- */

  const readCount = () => {
    try {
      return parseInt(localStorage.getItem(COUNT_KEY), 10) || 0;
    } catch (e) {
      return 0;
    }
  };

  const everyNth = () =>
    Math.max(1, parseInt((state.config && state.config.frequencyEvery) || 1, 10));

  // Counts every matching add to cart, whether or not the popup was shown, so
  // "every 3rd add" means exactly that. Non-mutating — prefetching needs to ask
  // the same question without consuming a turn.
  const canShowNow = () => {
    const frequency = state.config && state.config.frequency;
    if (!frequency || frequency === 'always') return true;
    if (window.Shopify && window.Shopify.designMode) return true;

    try {
      if (frequency === 'every_add') {
        return readCount() % everyNth() === 0;
      }
      if (frequency === 'session') {
        return sessionStorage.getItem(STORAGE_KEY) === null;
      }
      if (frequency === 'day') {
        const last = parseInt(localStorage.getItem(STORAGE_KEY), 10);
        if (!last) return true;
        return Date.now() - last > 86400000;
      }
    } catch (e) {
      // Private browsing or storage disabled — fail open rather than never showing.
      return true;
    }
    return true;
  };

  // Called once per matching add, at the moment the decision is taken.
  const consumeFrequency = () => {
    if (!state.config || state.config.frequency !== 'every_add') return;
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      localStorage.setItem(COUNT_KEY, String(readCount() + 1));
    } catch (e) {
      /* storage unavailable — nothing to do */
    }
  };

  const rememberShown = () => {
    const frequency = state.config && state.config.frequency;
    if (!frequency || frequency === 'always') return;
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      if (frequency === 'session') {
        sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
      } else if (frequency === 'day') {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      }
    } catch (e) {
      /* storage unavailable — nothing to do */
    }
  };

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  const getCartCount = () => {
    const node =
      document.querySelector(SELECTORS.cartCount) ||
      document.querySelector(SELECTORS.headerCount);
    if (!node) return 0;
    return parseInt(node.textContent.trim(), 10) || 0;
  };

  const getDrawer = () => document.querySelector(SELECTORS.drawer);

  const getModal = () => document.querySelector(SELECTORS.modal);

  const isQuickAddForm = productForm =>
    Boolean(
      productForm.closest('quick-add-to-cart') ||
        productForm.closest('quick-view-product') ||
        productForm.closest('.kt-modal--product')
    );

  const isConfigured = () =>
    Boolean(state.config && (state.config.triggers || []).length);

  const handleFromUrl = url =>
    (url || '').split('?')[0].split('/').filter(Boolean).pop() || '';

  // Whether we already know this product has an offer, without a round trip.
  // Product pages know because the section pre-rendered the offer into the
  // page; grids know because the offer was prefetched when the customer showed
  // intent. Anything else answers "no" and the drawer opens as normal — the
  // one thing that must never regress.
  const knownOffer = productForm => {
    if (!isConfigured()) return false;

    const url = productForm.getAttribute('data-product-url') || '';
    if (getPreRendered(handleFromUrl(url))) return true;

    return url ? state.matchCache.get(url) === true : false;
  };

  const sourceAllowed = productForm => {
    const config = state.config;
    if (!config) return false;
    if (isQuickAddForm(productForm)) {
      return config.triggerFromQuickAdd !== false;
    }
    return config.triggerFromProductPage !== false;
  };

  /* ----------------------------------------------------------------------
     Drawer control
     ---------------------------------------------------------------------- */

  const patchDrawer = () => {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('sidebar-drawer').then(() => {
      const constructor = customElements.get('sidebar-drawer');
      if (!constructor || constructor.prototype.__ktCrossSellPatched) return;

      const proto = constructor.prototype;
      const originalShow = proto.show;
      proto.__ktCrossSellPatched = true;

      proto.show = function patchedShow() {
        if (this.id === 'site-cart-sidebar' && state.holdDrawer) {
          // An offer is being resolved, or the popup is on screen. Remember
          // that the drawer wanted to open so it can be released afterwards.
          state.drawerWasRequested = true;
          return undefined;
        }
        return originalShow.apply(this, arguments);
      };
    });
  };

  // `open` left undefined means "do whatever the drawer was going to do".
  // Passing an explicit boolean overrides that.
  const releaseDrawer = open => {
    const shouldOpen =
      open === undefined ? state.drawerWasRequested : Boolean(open);

    clearTimeout(state.failsafeTimer);
    // Discard anything still in flight for the interaction being released.
    state.generation += 1;
    state.armedForm = null;
    state.holdDrawer = false;
    state.drawerWasRequested = false;
    state.busy = false;

    if (!shouldOpen) return;

    const drawer = getDrawer();
    if (!drawer || typeof drawer.show !== 'function') return;
    if (document.body.classList.contains('template-cart')) return;

    // Re-opening an already-open drawer would overwrite the theme's stored
    // previously-focused element and send focus to the wrong place on close.
    if (drawer.classList.contains('kt-sidebar--opened')) return;

    drawer.show();

    // The drawer stores whatever had focus when it opened, so it can restore
    // it on close. Opening from a closing popup would capture the popup's
    // close button, which is about to be hidden — leaving focus nowhere. Clear
    // it so the drawer simply skips the restore.
    if (window.KROWN) window.KROWN.previouslyFocused = null;

    if (window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher) {
      KROWN.functions.eventDispatcher('krown:cart-sidebar:show', {
        sidebarElement: drawer,
      });
    }
  };

  const abort = () => {
    releaseDrawer();
  };

  /* ----------------------------------------------------------------------
     Cache invalidation

     Offer content is rendered against the cart (products already in the cart
     are filtered out) and against live price and availability. Any cart
     change makes both the cache and the page-load pre-render stale, so both
     are dropped whenever the cart moves.
     ---------------------------------------------------------------------- */

  // Offers depend on what is in the cart, so cache entries are keyed by cart
  // contents as well as product. A cart change simply stops matching the old
  // key, which keeps caching useful without ever serving a stale offer.
  const cartKey = () => {
    const cartForm = document.getElementById('AjaxCartForm');
    if (!cartForm) return '';
    return Array.from(cartForm.querySelectorAll('[data-js-cart-item]'))
      .map(item => item.dataset.productId)
      .sort()
      .join(',');
  };

  const invalidateOffers = () => {
    state.generation += 1;
    const modal = getModal();
    const content = modal && modal.querySelector(SELECTORS.content);
    // The page-load pre-render was built against an older cart.
    if (content) content.removeAttribute('data-cross-sell-product');
  };

  /* ----------------------------------------------------------------------
     Rendering
     ---------------------------------------------------------------------- */

  const contentHasOffer = node =>
    Boolean(node && node.querySelector(SELECTORS.card));

  const getPreRendered = handle => {
    const modal = getModal();
    if (!modal) return null;
    const content = modal.querySelector(SELECTORS.content);
    if (!content) return null;
    if (content.getAttribute('data-cross-sell-product') !== handle) return null;
    if (!contentHasOffer(content)) return null;
    return content.innerHTML;
  };

  const fetchOffer = productUrl => {
    const key = `${productUrl}::${cartKey()}`;

    if (state.cache.has(key)) {
      return Promise.resolve(state.cache.get(key));
    }

    const separator = productUrl.indexOf('?') === -1 ? '?' : '&';
    const url = `${productUrl}${separator}section_id=${encodeURIComponent(
      state.config.sectionId
    )}`;

    // The cart drawer is held shut while this runs, so a stalled request must
    // not be allowed to hang. Give up quickly and let the drawer open instead.
    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), 4000)
      : null;

    return fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(response => {
        if (!response.ok) throw new Error('Cross-sell section request failed');
        return response.text();
      })
      .then(text => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = parsed.querySelector(SELECTORS.content);
        const html = contentHasOffer(content) ? content.innerHTML : '';
        state.cache.set(key, html);
        state.matchCache.set(productUrl, html !== '');
        return html;
      })
      .catch(() => {
        // Do not cache a failure — a timeout says nothing about whether an
        // offer exists, and caching it would suppress the popup for the rest
        // of the page view.
        return '';
      })
      .finally(() => {
        if (timer) clearTimeout(timer);
      });
  };

  // Closing the popup always takes the customer to the cart, however they
  // closed it — continue, no thanks, the X, the background or escape. The
  // popup interrupted an add to cart, so the cart is where they expect to end.
  const openDrawerAfterClose = () => true;

  // Bound once on the modal itself rather than on the buttons. The offer
  // markup is replaced every time the popup opens, so binding the buttons
  // directly means a missed bind leaves a dead button.
  const bindContent = modal => {
    if (modal.__ktContentBound) return;
    modal.__ktContentBound = true;

    modal.addEventListener('click', event => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      if (target.closest(SELECTORS.continueButton)) {
        event.preventDefault();
        state.forceDrawerOnClose = true;
        modal.hide();
        return;
      }

      if (target.closest(SELECTORS.dismissButton)) {
        event.preventDefault();
        state.forceDrawerOnClose = openDrawerAfterClose();
        modal.hide();
      }
    });
  };

  const showPopup = (html, productHandle) => {
    const modal = getModal();
    if (!modal || typeof modal.show !== 'function') {
      abort();
      return;
    }

    const content = modal.querySelector(SELECTORS.content);
    if (!content) {
      abort();
      return;
    }

    content.innerHTML = html;

    bindContent(modal);
    modal.show();
    rememberShown();
    clearTimeout(state.failsafeTimer);
    state.busy = false;
    state.popupOpen = true;

    // This content was rendered against the cart as it stood a moment ago.
    // The add that opened this popup has already changed the cart, so anything
    // cached or pre-rendered is now out of date and must not be reused.
    invalidateOffers();

    // The drawer is still held. It is released when the popup closes.
    if (window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher) {
      KROWN.functions.eventDispatcher('krown:cross-sell:show', {
        productHandle: productHandle,
        modalElement: modal,
      });
    }
  };

  /* ----------------------------------------------------------------------
     Modal close handling — releases the drawer whichever way it is closed
     ---------------------------------------------------------------------- */

  const watchModalClose = () => {
    if (!window.customElements || !customElements.whenDefined) return;

    customElements.whenDefined('modal-box').then(() => {
      const modal = getModal();
      if (!modal || modal.__ktCloseWatched) return;
      if (typeof modal.hide !== 'function') return;
      modal.__ktCloseWatched = true;

      const originalHide = modal.hide.bind(modal);

      modal.hide = function patchedHide() {
        const wasHeld = state.holdDrawer;
        const shouldOpen = state.forceDrawerOnClose || wasHeld;

        const result = originalHide.apply(this, arguments);

        state.forceDrawerOnClose = false;
        state.popupOpen = false;

        // Covers every close path — the X, the background, escape, or the
        // continue and dismiss buttons.
        if (wasHeld) {
          releaseDrawer(shouldOpen);
        }

        return result;
      };
    });
  };

  /* ----------------------------------------------------------------------
     Triggers
     ---------------------------------------------------------------------- */

  // Mirrors the guard in ProductForm.onSubmitHandler. When a required line
  // item property is empty the theme returns early without adding anything and
  // without firing any event — arming here would hold the drawer shut for a
  // customer who has not actually added anything.
  const willBeRejected = productForm => {
    const productPage = productForm.closest('[data-js-product-component]');
    if (!productPage) return false;

    // Follow the theme's own prefix rather than hardcoding it, so this keeps
    // matching if KT_PREFIX ever changes.
    const prefix = window.KT_PREFIX || 'kt-';

    return Array.from(
      productPage.querySelectorAll('[data-js-custom-property]')
    ).some(elm => {
      if (!elm.querySelector('[required]')) return false;

      if (elm.classList.contains(`${prefix}product-variants--text`)) {
        const text = elm.querySelector('input') || elm.querySelector('textarea');
        return Boolean(text) && text.value === '';
      }
      if (elm.classList.contains(`${prefix}product-variants--select`)) {
        const select = elm.querySelector('select');
        return Boolean(select) && select.value === '';
      }
      if (elm.classList.contains(`${prefix}product-variants--checkbox`)) {
        return !elm.querySelector('input:checked');
      }
      return false;
    });
  };

  const onSubmitCapture = event => {
    if (!state.config) return;

    const form = event.target;
    if (!form || typeof form.closest !== 'function') return;

    const productForm = form.closest('product-form');
    if (!productForm) return;

    // Never let an upsell add trigger another popup.
    if (productForm.hasAttribute('data-no-cross-sell')) return;

    // The popup only makes sense when the cart is ajax driven.
    if (!productForm.hasAttribute('data-ajax-cart')) return;

    // Already resolving an offer or showing one — leave that flow alone rather
    // than clobbering its state half way through. holdDrawer covers the window
    // between submit and the cart event, which busy does not.
    if (state.holdDrawer || state.busy || state.popupOpen) return;

    if (document.body.classList.contains('template-cart')) return;
    if (!sourceAllowed(productForm)) return;
    if (!knownOffer(productForm)) return;

    // Counted before the show/skip decision so the interval is measured in
    // add to carts, not in popups.
    const allowed = canShowNow();
    consumeFrequency();
    if (!allowed) return;
    if (willBeRejected(productForm)) return;

    state.armedForm = productForm;
    state.holdDrawer = true;
    state.drawerWasRequested = false;
    state.countBefore = getCartCount();

    // Safety net. If the add request fails outright the theme never dispatches
    // its cart events, and without this the drawer would stay blocked for the
    // rest of the page view. Release it if nothing has happened in time.
    clearTimeout(state.failsafeTimer);
    state.failsafeTimer = setTimeout(() => {
      if (state.holdDrawer && !state.popupOpen) {
        releaseDrawer();
      }
    }, 8000);
  };

  const onCartAdd = () => {
    const productForm = state.armedForm;

    // Any cart movement invalidates previously resolved offers.
    if (!productForm) {
      invalidateOffers();
      return;
    }

    // Consume the arm immediately. Without this, an add made from inside the
    // popup would come back through here and rebuild the popup under the
    // customer's cursor.
    state.armedForm = null;

    // Confirm the add actually succeeded. The theme still fires its events
    // when Shopify rejects the line (out of stock), and a popup there would
    // be wrong.
    if (getCartCount() <= state.countBefore) {
      invalidateOffers();
      abort();
      return;
    }

    state.busy = true;

    const productUrl = productForm.getAttribute('data-product-url') || '';
    const handle = productUrl.split('?')[0].split('/').filter(Boolean).pop() || '';

    const preRendered = getPreRendered(handle);
    if (preRendered) {
      // Remember that this product has an offer. The pre-render is consumed
      // once, so without this a second add of the same product on the same
      // page would no longer qualify and the popup would never return.
      if (productUrl) state.matchCache.set(productUrl, true);
      showPopup(preRendered, handle);
      return;
    }

    if (!productUrl || !state.config.sectionId) {
      abort();
      return;
    }

    const generation = state.generation;

    fetchOffer(productUrl).then(html => {
      // The cart moved, or the failsafe already released the drawer, while
      // this was in flight. The offer was rendered against a cart that no
      // longer exists, so drop it — but still release the drawer, or the
      // customer is left with neither a popup nor their cart.
      if (generation !== state.generation) {
        abort();
        return;
      }

      if (!html) {
        abort();
        return;
      }
      showPopup(html, handle);
    });
  };

  // Fired by <upsell-add-to-cart> in component-cart-upsell.js once an add from
  // inside the popup has succeeded.
  const onUpsellAdded = event => {
    const detail = event.detail || {};
    if (detail.source !== 'popup') return;

    const modal = getModal();
    if (!modal) return;

    if (state.config && state.config.afterAdd === 'close') {
      state.forceDrawerOnClose = true;
      modal.hide();
    }
  };

  /* ----------------------------------------------------------------------
     Theme editor support
     ---------------------------------------------------------------------- */

  const bindDesignMode = () => {
    if (!window.Shopify || !window.Shopify.designMode) return;

    document.addEventListener('shopify:section:load', event => {
      if (!event.target.classList.contains('mount-cross-sell-popup')) return;
      readConfig();
      watchModalClose();
      state.cache.clear();
    });

    document.addEventListener('shopify:section:select', event => {
      if (!event.target.classList.contains('mount-cross-sell-popup')) return;
      const modal = getModal();
      if (!modal || typeof modal.show !== 'function') return;

      // Showing an empty dialog just looks broken to the merchant. Only
      // preview when there is actually an offer rendered to preview.
      if (!contentHasOffer(modal.querySelector(SELECTORS.content))) return;

      bindContent(modal);
      modal.show();
    });

    document.addEventListener('shopify:section:deselect', event => {
      if (!event.target.classList.contains('mount-cross-sell-popup')) return;
      const modal = getModal();
      if (modal && typeof modal.hide === 'function') modal.hide();
    });

    document.addEventListener('shopify:block:select', event => {
      const section = event.target.closest
        ? event.target.closest('.mount-cross-sell-popup')
        : null;
      if (!section) return;
      const modal = getModal();
      if (modal && typeof modal.show === 'function') modal.show();
    });
  };

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  // Prefetching on intent replaces what used to be a data-product-collections
  // attribute on every product card. That attribute forced product.collections
  // to be evaluated for all 48 products of a collection page, which is a heavy
  // server side lookup; this moves the cost to the one product the customer is
  // actually reaching for.
  const prefetchOffer = event => {
    if (!isConfigured()) return;

    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const productForm = target.closest('product-form[data-product-url]');
    if (!productForm) return;
    if (productForm.hasAttribute('data-no-cross-sell')) return;
    if (!productForm.hasAttribute('data-ajax-cart')) return;
    if (!sourceAllowed(productForm)) return;
    if (!canShowNow()) return;

    const url = productForm.getAttribute('data-product-url');
    if (!url || state.matchCache.has(url)) return;

    // Marked before the request resolves so repeated hovers don't stack.
    state.matchCache.set(url, null);
    fetchOffer(url);
  };

  const init = () => {
    readConfig();
    patchDrawer();
    watchModalClose();

    document.addEventListener('submit', onSubmitCapture, true);
    document.addEventListener('pointerenter', prefetchOffer, true);
    document.addEventListener('pointerdown', prefetchOffer, true);
    document.addEventListener('focusin', prefetchOffer);
    document.addEventListener('krown:cart:add', onCartAdd);
    document.addEventListener('krown:cross-sell:add', onUpsellAdded);

    bindDesignMode();
  };

  window.KTCrossSell = {
    isPending: () => Boolean(state.holdDrawer),
    release: () => releaseDrawer(true),
    refreshConfig: readConfig,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
